import { useRef, useState } from 'react'
import { Button, Card, CardBody, Input } from '../../design-system/primitives/index.js'
import StepIndicator from '../../design-system/patterns/StepIndicator.jsx'
import { useGuestKitchen } from '../../contexts/GuestKitchenContext.jsx'
import { scrollElementToScrollRootTop } from '../../utils/scrollElementToScrollRootTop.js'

const NAME_REQUIRED = 'Enter your name to continue.'

function selectedCuisineNames(recipe = {}) {
  if (Array.isArray(recipe.cuisine_tags) && recipe.cuisine_tags.length) {
    return recipe.cuisine_tags
  }
  const saved = recipe.cuisine_preferences
  if (Array.isArray(saved)) return saved.filter((name) => String(name).trim())
  if (saved && typeof saved === 'object') {
    return Object.entries(saved).filter(([, on]) => on).map(([name]) => name)
  }
  return []
}

const CUISINES = [
  'American', 'Chinese', 'French', 'Greek', 'Indian', 'Italian',
  'Japanese', 'Korean', 'Mediterranean', 'Mexican', 'Thai',
]

export default function OnboardingPreferences({ onSelectStep, onContinue }) {
  const { kitchen, update } = useGuestKitchen()
  const existing = kitchen?.preferences || {}
  const [firstName, setFirstName] = useState(existing.firstName || '')
  const [cuisines, setCuisines] = useState(() => new Set(selectedCuisineNames(existing.recipe)))
  const householdMembers = Object.entries(existing.family_members || {})
    .filter(([key]) => key !== 'you')
    .map(([, member]) => String(member?.name || '').trim())
    .filter(Boolean)
  const [dietary, setDietary] = useState({
    vegetarian: Boolean(existing.dietary?.vegetarian),
    vegan: Boolean(existing.dietary?.vegan),
    gluten_free: Boolean(existing.dietary?.gluten_free),
  })
  const [allergies, setAllergies] = useState((existing.dietary?.allergies || []).join(', '))
  const [nameError, setNameError] = useState('')
  const [saveError, setSaveError] = useState('')
  const nameCardRef = useRef(null)
  const nameInputRef = useRef(null)

  const canContinue = firstName.trim().length > 0

  function persistProfiles(currentFirstName = firstName, members = householdMembers) {
    const name = currentFirstName.trim()
    update((current) => ({
      ...current,
      preferences: {
        ...(current.preferences || {}),
        ...(name ? { firstName: name } : {}),
        family_members: Object.fromEntries([
          ...(name ? [['you', { name }]] : []),
          ...members.map((memberName) => [
            memberName.toLowerCase().replace(/\s+/g, '_'),
            { name: memberName },
          ]),
        ]),
      },
    }))
  }
  function buildPreferences() {
    const selectedCuisines = Array.from(cuisines)
    return {
      firstName: firstName.trim(),
      family_members: Object.fromEntries([
        ['you', { name: firstName.trim() }],
        ...householdMembers.map((name) => [name.toLowerCase().replace(/\s+/g, '_'), { name }]),
      ]),
      recipe: {
        cuisine_preferences: selectedCuisines,
        cuisine_tags: selectedCuisines,
      },
      dietary: {
        ...dietary,
        allergies: allergies.split(',').map((value) => value.trim()).filter(Boolean),
      },
    }
  }

  function showNameRequired() {
    setSaveError('')
    setNameError(NAME_REQUIRED)
    scrollElementToScrollRootTop(nameCardRef.current)
    nameInputRef.current?.focus({ preventScroll: true })
  }

  function saveAndContinue() {
    if (!canContinue) {
      showNameRequired()
      return
    }
    try {
      update((current) => {
        const preferences = buildPreferences()
        const shopping = current.preferences?.shopping
        if (shopping && Object.keys(shopping).length) preferences.shopping = shopping
        const savedRecipe = current.preferences?.recipe
        if (savedRecipe && typeof savedRecipe.max_total_time_minutes === 'number') {
          preferences.recipe.max_total_time_minutes = savedRecipe.max_total_time_minutes
        }
        const savedWeeknight = savedRecipe?.prefer_weeknight_friendly
        if (preferences.recipe && typeof savedWeeknight === 'boolean') {
          preferences.recipe.prefer_weeknight_friendly = savedWeeknight
        }
        const savedDislikes = current.preferences?.dietary?.dislikes
        if (Array.isArray(savedDislikes) && savedDislikes.length) {
          preferences.dietary.dislikes = savedDislikes
        }
        return { ...current, preferences }
      })
    } catch {
      setNameError('')
      setSaveError('Reconnect to continue.')
      return
    }
    onContinue?.()
  }

  function toggleCuisine(name) {
    setCuisines((current) => {
      const next = new Set(current)
      if (next.has(name)) next.delete(name)
      else next.add(name)
      return next
    })
  }

  return (
    <section className="onboarding-preferences" aria-label="Kitchen setup step 3 of 4">
      <StepIndicator
        steps={['Pantry', 'Recipes', 'Preferences', 'Plan']}
        activeIndex={2}
        label="Kitchen setup progress"
        onStepSelect={(nextStep) => {
          persistProfiles()
          onSelectStep?.(nextStep)
        }}
      />

      <h1 className="onboarding-preferences__title">You, and what you like to eat.</h1>
      <Card className="onboarding-preferences__name-card" ref={nameCardRef}>
        <CardBody>
          <label className="onboarding-preferences__subhead" htmlFor="guest-first-name">First name</label>
          <Input
            ref={nameInputRef}
            id="guest-first-name"
            value={firstName}
            onChange={(event) => {
              setFirstName(event.target.value)
              setNameError('')
            }}
            autoComplete="given-name"
            invalid={Boolean(nameError)}
            aria-describedby={nameError ? 'guest-first-name-error' : undefined}
          />
          {nameError ? (
            <p id="guest-first-name-error" className="onboarding-preferences__error" role="alert">
              {nameError}
            </p>
          ) : null}
        </CardBody>
      </Card>

      <Card>
        <CardBody className="onboarding-preferences__settings">
          <div>
            <h4 className="onboarding-preferences__subhead">Cuisines you enjoy</h4>
            <div className="onboarding-preferences__cuisine-grid">
              {[...CUISINES, ...Array.from(cuisines).filter((name) => !CUISINES.includes(name))].map((name) => {
                const selected = cuisines.has(name)
                return (
                  <button
                    key={name}
                    type="button"
                    className={`onboarding-preferences__cuisine${selected ? ' onboarding-preferences__cuisine--on' : ''}`}
                    onClick={() => toggleCuisine(name)}
                    aria-pressed={selected}
                  >
                    <span className="onboarding-preferences__cuisine-mark" aria-hidden="true">✓</span>
                    <span>{name}</span>
                  </button>
                )
              })}
            </div>
          </div>
          <fieldset className="onboarding-preferences__checks">
            <legend>How you eat</legend>
            {[
              ['vegetarian', 'Vegetarian'],
              ['vegan', 'Vegan'],
              ['gluten_free', 'Gluten-free'],
            ].map(([key, label]) => (
              <label key={key}>
                <input
                  type="checkbox"
                  checked={dietary[key]}
                  onChange={(event) => setDietary((current) => ({ ...current, [key]: event.target.checked }))}
                />
                {label}
              </label>
            ))}
          </fieldset>
          <div>
            <label className="onboarding-preferences__field-label" htmlFor="guest-allergies">Allergies</label>
            <Input id="guest-allergies" value={allergies} onChange={(event) => setAllergies(event.target.value)} placeholder="e.g., peanuts" />
          </div>
        </CardBody>
      </Card>

      <footer className="onboarding-step-footer">
        <Button
          variant="primary"
          onClick={saveAndContinue}
          aria-describedby={!canContinue ? 'guest-first-name-hint' : undefined}
        >
          Continue to Plan
        </Button>
        {!canContinue ? (
          <p id="guest-first-name-hint" className="recipe-start__status">
            {NAME_REQUIRED}
          </p>
        ) : null}
        {saveError ? (
          <p className="onboarding-preferences__error" role="alert">{saveError}</p>
        ) : null}
      </footer>
    </section>
  )
}
